/*
  Integrated search bar (MU-3054) — Phase 1 UI integration.

  <mu-integrated-search>:
  - On input focus, open the suggestions panel (if configured) and darken the page content
    (overlay is `body.mu-search-open::before`, below the header group, so the announcement bar,
    header and search/panel stay highlighted). A click outside the component or the Escape key
    closes it. It also closes on scroll (header IntersectionObserver), so browsing dismisses it.
  - The on-screen keyboard is dropped on the first touch that isn't the input (see
    onDocumentPointerDown). That's the fix for the "sections stop fading in mid-fling" bug: on iOS,
    scrolling the page while the input's keyboard is up stalls the theme's IntersectionObserver-
    driven section reveals (animations.js) until the keyboard/scroll settles. Dismissing it on first
    contact — before any scroll starts — keeps the reveals firing. We don't lock body scroll.
  - A compact search trigger ([data-mu-search-compact]) lives in the header and is shown by CSS only
    while the header floats (sticky-revealed), where the full bar is hidden. Clicking it scrolls to
    the top and focuses the full search bar.

  See .context/docs/javascript-guidelines.md for the vanilla-JS conventions this follows.
*/

// Drives the body-level darken overlay (CSS `body.mu-search-open::before`).
const BODY_OPEN_CLASS = 'mu-search-open';

// Animated typewriter placeholder timing (ms).
const TW_HOLD = 3000; // how long each phrase stays before the next one is typed
const TW_TYPE = 70; // per-character typing speed
const TW_DELETE = 20; // per-character deleting speed (2× faster than typing)

// Search mode sections + debounce for the fetch. By default (settings.integrated_search_products_source
// = "storefront") products come from the storefront search route (RESULTS_SECTION_ID) so the panel
// ranks identically to the results page, and the Predictive Search API (PREDICTIVE_SECTION_ID) only
// supplies the query/collection suggestion pills. When that setting is "predictive", PREDICTIVE_SECTION_ID
// alone supplies products, pills, no-results and the "search for" link — see fetchSearch().
const RESULTS_SECTION_ID = 'mu-integrated-search-results';
const PREDICTIVE_SECTION_ID = 'mu-integrated-predictive-search';
const SEARCH_DEBOUNCE = 250;

// Recent searches (localStorage), see the "Recent searches" section below.
const RECENT_SEARCHES_KEY = 'mu-integrated-search-recent-searches';
const RECENT_SEARCHES_MAX = 6;

// How far past the header's own bottom edge (px) the page must scroll before the open panel
// auto-closes — see initHeaderObserver. 0 = close as soon as the header is fully out of view.
const SCROLL_CLOSE_OFFSET = 0;

// iOS/Android auto-zoom the viewport when a focused input's font-size is under 16px. Rather than
// bump the input's font-size (which would change how it looks), cap the viewport's max zoom only
// while this input is focused — see initZoomPrevention below.
const VIEWPORT_NO_ZOOM_SUFFIX = ', maximum-scale=1';

function readRecentSearches() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(RECENT_SEARCHES_KEY));
    return Array.isArray(parsed) ? parsed.filter((term) => typeof term === 'string' && term) : [];
  } catch (error) {
    return [];
  }
}

function writeRecentSearches(terms) {
  try {
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(terms));
  } catch (error) {
    // Storage unavailable (privacy mode, quota, disabled) — the feature just degrades to a no-op.
  }
}

function addRecentSearch(term) {
  const deduped = readRecentSearches().filter((saved) => saved.toLowerCase() !== term.toLowerCase());
  deduped.unshift(term);
  writeRecentSearches(deduped.slice(0, RECENT_SEARCHES_MAX));
}

class MuIntegratedSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    if (!this.input) return;

    this.panel = this.querySelector('[data-mu-search-panel]');
    this.isActive = false;

    // The bar always renders but is runtime-gated on the `muun-integrated-search` html class (A/B
    // — see .context/docs/ab-testing-integrated-search.md). CSS hides everything when it's absent;
    // this mirrors that at the JS level via activate()/deactivate() below. A MutationObserver
    // (not a one-shot check here in connectedCallback) is required because an A/B tool can add or
    // remove the class at any point after this element has already connected — a one-shot check
    // would only ever see whatever state the class happened to be in at parse time and never wire
    // up (or tear down) behaviour if it changes later.
    this.onHtmlClassChange = this.syncActivation.bind(this);
    this.htmlObserver = new MutationObserver(this.onHtmlClassChange);
    this.htmlObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    this.syncActivation();
  }

  disconnectedCallback() {
    if (this.htmlObserver) this.htmlObserver.disconnect();
    this.deactivate();
  }

  // Reconciles wiring with the current flag state — called once at connect and again on every
  // `muun-integrated-search` class change on <html>.
  syncActivation() {
    const shouldBeActive = document.documentElement.classList.contains('muun-integrated-search');
    if (shouldBeActive && !this.isActive) this.activate();
    else if (!shouldBeActive && this.isActive) this.deactivate();
  }

  activate() {
    if (this.isActive) return;
    this.isActive = true;

    // Suggestions panel + overlay (only wired when there's something to show).
    if (this.panel) {
      this.isOpen = false;
      this.onFocus = this.open.bind(this);
      this.onDocumentPointerDown = this.onDocumentPointerDown.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onWindowScroll = this.close.bind(this);
      this.initHeaderObserver();
      this.input.addEventListener('focus', this.onFocus);
    }

    // Compact trigger shown while the header floats.
    this.onCompactClick = this.onCompactClick.bind(this);
    this.compactTriggers = document.querySelectorAll('[data-mu-search-compact]');
    this.compactTriggers.forEach((trigger) => trigger.addEventListener('click', this.onCompactClick));

    // Animated typewriter placeholder cycling through the recommended search terms.
    this.initPlaceholderTypewriter();

    // Recent searches: capture the query if we just landed on the results page, and render the
    // saved list (if any) as a third panel section.
    this.initRecentSearches();

    // Search mode: swap the idle suggestions for live predictive results while the shopper types.
    this.initSearchMode();

    // Prevent the mobile viewport auto-zoom on focus, without changing the input's font-size.
    this.initZoomPrevention();
  }

  deactivate() {
    if (!this.isActive) return;
    this.isActive = false;

    this.close();
    if (this.headerObserver) this.headerObserver.disconnect();
    if (this.input && this.onFocus) this.input.removeEventListener('focus', this.onFocus);
    if (this.compactTriggers) {
      this.compactTriggers.forEach((trigger) => trigger.removeEventListener('click', this.onCompactClick));
    }
    this.stopTypewriter();
    if (this.input && this.onTwFocus) {
      this.input.removeEventListener('focus', this.onTwFocus);
      this.input.removeEventListener('blur', this.onTwBlur);
    }
    if (this.clearRecentButton && this.onClearRecent) {
      this.clearRecentButton.removeEventListener('click', this.onClearRecent);
    }
    if (this.input && this.onSearchInput) {
      this.input.removeEventListener('input', this.onSearchInput);
      this.input.removeEventListener('focus', this.onSearchFocus);
      this.input.form.removeEventListener('reset', this.onSearchFormReset);
    }
    clearTimeout(this.searchTimer);
    if (this.searchAbort) this.searchAbort.abort();
    if (this.input && this.onZoomFocus) {
      this.input.removeEventListener('focus', this.onZoomFocus);
      this.input.removeEventListener('blur', this.onZoomBlur);
    }
  }

  // Temporarily caps the viewport's max zoom while this input is focused (restoring the original
  // meta on blur), so tapping it doesn't trigger the mobile auto-zoom — without needing to bump
  // the input's own font-size above the 16px threshold that behaviour is keyed on.
  initZoomPrevention() {
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    if (!viewportMeta) return;
    const originalContent = viewportMeta.getAttribute('content');

    this.onZoomFocus = () => viewportMeta.setAttribute('content', originalContent + VIEWPORT_NO_ZOOM_SUFFIX);
    this.onZoomBlur = () => viewportMeta.setAttribute('content', originalContent);
    this.input.addEventListener('focus', this.onZoomFocus);
    this.input.addEventListener('blur', this.onZoomBlur);
  }

  open() {
    if (this.isOpen) return;
    // Nothing to show (no collections/terms configured, no recent searches yet) — focusing the
    // field shouldn't darken/lock the page for an empty box.
    if (this.panel && this.panel.classList.contains('mu-integrated-search__panel--empty')) return;
    this.isOpen = true;
    this.classList.add('is-open');
    this.input.setAttribute('aria-expanded', 'true');
    document.body.classList.add(BODY_OPEN_CLASS);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onKeydown);
    // Close on scroll, so browsing the page dismisses the search. onDocumentPointerDown drops the
    // keyboard on the first contact, before any scroll starts, so on iOS this observer isn't frozen
    // by the focused-input scroll stall (see the note there).
    if (this.headerObserver && this.header) this.headerObserver.observe(this.header);
    // Direct fallback: the line above only closes once .section-header itself leaves the viewport,
    // which depends on a separate component (StickyHeader) actually hiding it first — not
    // guaranteed to happen promptly (or at all) for every scroll. A plain window scroll listener
    // closes unconditionally on any page scroll, independent of that. `scroll` doesn't bubble, so
    // this never fires from scrolling inside the panel's own results (overflow-y: auto) — only a
    // real page scroll reaches it.
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    if (this.input) this.input.setAttribute('aria-expanded', 'false');
    document.body.classList.remove(BODY_OPEN_CLASS);
    document.removeEventListener('pointerdown', this.onDocumentPointerDown);
    document.removeEventListener('keydown', this.onKeydown);
    if (this.onWindowScroll) window.removeEventListener('scroll', this.onWindowScroll);
    if (this.headerObserver) this.headerObserver.disconnect();
    // Cancel any predictive work still in flight. Without this, a debounced fetch queued just
    // before the dismissal still resolves into renderSearchResults -> enterSearchMode -> open(),
    // popping the panel and overlay straight back open after Escape / outside-tap / scroll-close.
    clearTimeout(this.searchTimer);
    if (this.searchAbort) this.searchAbort.abort();
  }

  // Watches the header with an IntersectionObserver (the same primitive the theme's own
  // scroll-reveal animations use, animations.js) and closes the panel once it scrolls out of view.
  initHeaderObserver() {
    this.header = document.querySelector('.section-header');
    if (!this.header || typeof IntersectionObserver === 'undefined') return;
    // rootMargin shifts the trigger point via SCROLL_CLOSE_OFFSET: 0 = close once the header's
    // bottom edge is fully above the viewport top; positive = close a bit earlier (while the header
    // still has that many px left showing); negative = allow that many extra px to scroll past
    // first. The panel only opens on input focus and the input lives inside the header, so the
    // header is always on screen at open() and observe()'s initial callback reports it intersecting
    // (no false close). After that, close as soon as it leaves.
    this.headerObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[entries.length - 1].isIntersecting) this.close();
      },
      { rootMargin: `${-SCROLL_CLOSE_OFFSET}px 0px 0px 0px` },
    );
  }

  onDocumentPointerDown(event) {
    // "Outside" means outside both visible pieces — the field itself and the open dropdown —
    // not outside the whole <mu-integrated-search> element, which is a full-width block with
    // empty side margins around the centred pill (.page-width). Checking containment against
    // that full element left those margins as a dead zone: visually outside the search, but
    // still inside the component, so clicks there did nothing.
    const inField = this.input.form.contains(event.target);
    const inPanel = this.panel && this.panel.contains(event.target);
    if (inField || inPanel) return;

    // Drop the on-screen keyboard before the close/scroll below. This is the fix for the
    // "sections don't fade in while flinging" bug: on iOS, scrolling the page while the input's
    // keyboard is up stalls the theme's section-reveal IntersectionObservers (animations.js) for
    // the whole momentum fling — confirmed on-device, dismissing the keyboard first makes the
    // very same scroll behave. pointerdown fires on contact, before any drag develops, so the
    // keyboard is already on its way out before a scroll (or its momentum) begins.
    this.input.blur();
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      this.input.blur();
    }
  }

  onCompactClick() {
    // Scroll to top so the sticky header resets and the full bar becomes visible, then focus.
    window.scrollTo({ top: 0, behavior: 'auto' });
    // Two frames lets the sticky-header scroll handler drop its classes before we focus.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.input.focus());
    });
  }

  // --- Recent searches (localStorage) ---------------------------------------------------------
  // Persists terms the shopper has actually searched, captured when they land on the results
  // page with a query, so they can quickly repeat one. Rendered as a third panel section, below
  // the merchant-configured collections/terms, using the same pill markup and styles.
  initRecentSearches() {
    this.captureCurrentSearch();

    this.recentSection = this.querySelector('[data-mu-recent-searches]');
    if (!this.recentSection) return;

    this.recentList = this.recentSection.querySelector('[data-mu-recent-list]');
    this.clearRecentButton = this.recentSection.querySelector('[data-mu-clear-recent]');
    if (this.clearRecentButton) {
      this.onClearRecent = () => {
        writeRecentSearches([]);
        this.renderRecentSearches();
      };
      this.clearRecentButton.addEventListener('click', this.onClearRecent);
    }

    this.renderRecentSearches();
  }

  // If this page load is the search results page with a query (i.e. a search was just carried
  // out — via this bar, a suggestion pill, or the legacy search), save the term.
  captureCurrentSearch() {
    const searchPath = this.dataset.searchUrl;
    if (!searchPath || window.location.pathname !== searchPath) return;

    const term = new URLSearchParams(window.location.search).get('q');
    if (term && term.trim()) addRecentSearch(term.trim());
  }

  renderRecentSearches() {
    const terms = readRecentSearches();
    const searchUrl = this.dataset.searchUrl || '/search';

    this.recentList.innerHTML = '';
    terms.forEach((term) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = `${searchUrl}?q=${encodeURIComponent(term)}`;
      link.className =
        'mu-integrated-search__term tw-text-semantic-text-primary tw-border tw-border-semantic-border-subtle';
      link.textContent = term;
      item.appendChild(link);
      this.recentList.appendChild(item);
    });

    this.recentSection.classList.toggle('hidden', terms.length === 0);
    this.updatePanelEmptyState();
  }

  // Idle-mode empty check: the panel markup is always rendered (recent searches are unknown at
  // Liquid-render time), so hide it entirely when idle mode has no visible section — focusing the
  // field then never opens an empty box. Search mode manages its own visibility (it always has
  // results or a "no results" message), so it's excluded here.
  updatePanelEmptyState() {
    if (!this.panel) return;
    if (this.classList.contains('mu-search-mode')) return;
    const hasVisibleSection = !!this.panel.querySelector(
      '[data-mu-search-idle] .mu-integrated-search__section:not(.hidden)',
    );
    this.panel.classList.toggle('mu-integrated-search__panel--empty', !hasVisibleSection);
    // e.g. clearing the last recent search while open — the panel just vanished, so drop the
    // overlay/scroll-lock with it instead of trapping the shopper on a darkened, locked page.
    if (!hasVisibleSection) this.close();
  }

  // --- Search mode (live results) -------------------------------------------------------------
  // While the shopper types, replace the idle suggestions with live results for the query:
  // products from the mu-integrated-search-results section (storefront search, same ranking as
  // the results page) plus suggestion pills from mu-integrated-predictive-search. Clearing the
  // field returns to idle mode.
  initSearchMode() {
    this.idleContainer = this.querySelector('[data-mu-search-idle]');
    this.resultsContainer = this.querySelector('[data-mu-search-results]');
    this.predictiveUrl = this.dataset.predictiveUrl;
    this.resultsUrl = this.dataset.searchUrl || '/search';
    this.productsSource = this.dataset.productsSource === 'predictive' ? 'predictive' : 'storefront';
    // Predictive mode needs the suggest endpoint (it supplies everything); storefront mode needs
    // the search route (the suggest endpoint is optional there, pills only). Without the one that
    // mode actually depends on there is nothing to show, so bail and leave the panel in idle mode.
    const requiredUrl = this.productsSource === 'predictive' ? this.predictiveUrl : this.resultsUrl;
    if (!this.resultsContainer || !requiredUrl) return;

    // Null-prototype: a plain {} would report inherited members (`constructor`, `toString`,
    // `valueOf`, …) as cache hits and render native-code text into the panel.
    this.searchCache = Object.create(null);
    this.searchAbort = null;
    this.searchTimer = null;

    this.onSearchInput = this.onSearchInput.bind(this);
    // Re-fetch on focus if the field already holds a query (e.g. returning to the page).
    this.onSearchFocus = () => {
      if (this.input.value.trim()) this.onSearchInput();
    };
    this.input.addEventListener('input', this.onSearchInput);
    this.input.addEventListener('focus', this.onSearchFocus);

    // The clear (x) button does a native form reset, which empties the field without firing an
    // `input` event — onSearchInput never runs, so search mode would otherwise stay stuck showing
    // stale results. Listen to the form's own reset event to exit explicitly.
    this.onSearchFormReset = () => this.exitSearchMode();
    this.input.form.addEventListener('reset', this.onSearchFormReset);
  }

  onSearchInput() {
    const query = this.input.value.trim();
    clearTimeout(this.searchTimer);

    if (!query) {
      this.exitSearchMode();
      return;
    }
    this.searchTimer = setTimeout(() => this.fetchSearch(query), SEARCH_DEBOUNCE);
  }

  // Fetches the panel's content and stitches it together. Two mutually exclusive sources (see
  // settings.integrated_search_products_source in the theme customizer):
  //
  //   "storefront" (default) - two parallel requests:
  //     products  <- /search?…&section_id=mu-integrated-search-results   (storefront search)
  //     pills     <- /search/suggest?…&section_id=…predictive-search     (Predictive Search API)
  //     Products deliberately come from the real search route so the panel ranks exactly like
  //     templates/search.json — the two engines disagree noticeably otherwise. The Predictive
  //     Search API is still the only source of query/collection suggestions, hence the second
  //     request.
  //
  //   "predictive" - one request to the Predictive Search API alone, which then supplies
  //     products, pills, the no-results message and the "search for" link all at once (kept as a
  //     merchant-facing option for comparing the two engines).
  //
  // Either way the fetched section(s) carry the same marker attributes
  // (data-mu-results-products/-empty/-more, data-mu-suggest-terms), so they're extracted and
  // stitched together identically regardless of source.
  fetchSearch(query) {
    if (this.searchCache[query] != null) {
      this.renderSearchResults(this.searchCache[query]);
      return;
    }

    if (this.searchAbort) this.searchAbort.abort();
    this.searchAbort = new AbortController();
    const { signal } = this.searchAbort;

    // The pills are a nice-to-have: a failing suggest request degrades to products-only rather
    // than taking the whole panel down with it, so it resolves to an empty fragment instead of
    // rejecting. AbortError still has to propagate, or an abort would render a half-empty panel.
    const fetchSection = (url, sectionId, optional) =>
      fetch(url, { signal })
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          return response.text();
        })
        .then((text) =>
          new DOMParser().parseFromString(text, 'text/html').querySelector(`#shopify-section-${sectionId}`),
        )
        .catch((error) => {
          if (optional && error.name !== 'AbortError') return null;
          throw error;
        });

    let primaryFetch;
    let suggestFetch;
    if (this.productsSource === 'predictive') {
      const predictiveParams = new URLSearchParams({
        q: query,
        section_id: PREDICTIVE_SECTION_ID,
        'resources[type]': 'product,query,collection',
        // 10 is the Predictive Search API's max per resource type.
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
      });
      primaryFetch = fetchSection(`${this.predictiveUrl}?${predictiveParams}`, PREDICTIVE_SECTION_ID, false);
      suggestFetch = Promise.resolve(null);
    } else {
      const resultsParams = new URLSearchParams({
        q: query,
        // Same prefix behaviour as the results page, so a half-typed last word still matches.
        'options[prefix]': 'last',
        // Articles and pages would push products out of a 10-item preview.
        type: 'product',
        section_id: RESULTS_SECTION_ID,
      });
      const suggestParams = new URLSearchParams({
        q: query,
        section_id: PREDICTIVE_SECTION_ID,
        // Products come from the search route; this request only supplies the "Vorschläge"
        // pills, which the `search` object cannot express.
        'resources[type]': 'query,collection',
        'resources[limit]': '10',
        'resources[limit_scope]': 'each',
      });
      primaryFetch = fetchSection(`${this.resultsUrl}?${resultsParams}`, RESULTS_SECTION_ID, false);
      suggestFetch = fetchSection(`${this.predictiveUrl}?${suggestParams}`, PREDICTIVE_SECTION_ID, true);
    }

    Promise.all([primaryFetch, suggestFetch])
      .then(([primarySection, suggestSection]) => {
        const products = primarySection?.querySelector('[data-mu-results-products]');
        const empty = primarySection?.querySelector('[data-mu-results-empty]');
        const more = primarySection?.querySelector('[data-mu-results-more]');
        const terms = (suggestSection || primarySection)?.querySelector('[data-mu-suggest-terms]');

        // Only claim "nothing found" when the pills came back empty as well — suggestions alone
        // are still a useful answer, and pairing them with a no-results line reads as a bug.
        const parts = [
          products?.outerHTML,
          products || terms ? '' : empty?.outerHTML,
          terms?.outerHTML,
          more?.outerHTML,
        ];
        const markup = parts.filter(Boolean).join('');

        // Extraction failed (unexpected markup) — fall back to idle rather than opening a blank
        // panel, and don't cache the miss, which would keep this query broken until reload.
        if (!markup) {
          if (query === this.input.value.trim()) this.exitSearchMode();
          return;
        }
        this.searchCache[query] = markup;
        // Ignore stale responses that resolved after the query moved on.
        if (query === this.input.value.trim()) this.renderSearchResults(markup);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        // On failure just fall back to idle rather than showing a broken panel.
        this.exitSearchMode();
      });
  }

  renderSearchResults(markup) {
    this.resultsContainer.innerHTML = markup;
    this.enterSearchMode();
  }

  enterSearchMode() {
    this.classList.add('mu-search-mode');
    if (this.idleContainer) this.idleContainer.classList.add('hidden');
    this.resultsContainer.classList.remove('hidden');
    // Pause the placeholder animation and make sure the panel is visible.
    this.stopTypewriter(true);
    if (this.panel) this.panel.classList.remove('mu-integrated-search__panel--empty');
    this.open();
  }

  exitSearchMode() {
    if (!this.classList.contains('mu-search-mode')) return;
    this.classList.remove('mu-search-mode');
    this.resultsContainer.classList.add('hidden');
    this.resultsContainer.innerHTML = '';
    if (this.idleContainer) this.idleContainer.classList.remove('hidden');
    // Recompute whether idle mode has anything to show (may close the panel if empty).
    this.updatePanelEmptyState();
  }

  // --- Animated typewriter placeholder -------------------------------------------------------
  // Cycles the input placeholder through the recommended search terms, deleting the current
  // phrase and typing the next one character by character. Starts from the static base
  // placeholder ("Wonach suchst du?") and loops. Pauses while the field is focused or has a
  // value; falls back to a plain swap when the user prefers reduced motion.
  initPlaceholderTypewriter() {
    // The recommended terms are already rendered as the panel's "popular terms" pills.
    const terms = Array.from(this.querySelectorAll('.mu-integrated-search__term'))
      .map((el) => el.textContent.trim())
      .filter(Boolean);
    if (!terms.length) return;

    this.basePlaceholder = this.input.getAttribute('placeholder') || '';
    this.twPhrases = [this.basePlaceholder, ...terms].filter(Boolean);
    // Nothing to rotate through (e.g. no base placeholder configured and a single term) — skip
    // the animation instead of deleting/retyping the same text forever.
    if (this.twPhrases.length <= 1) return;
    this.twReduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // On focus, pause the animation and restore the clean base placeholder (the field itself
    // stays empty); on blur, resume the rotation.
    this.onTwFocus = () => this.stopTypewriter(true);
    this.onTwBlur = () => this.startTypewriter();
    this.input.addEventListener('focus', this.onTwFocus);
    this.input.addEventListener('blur', this.onTwBlur);

    this.startTypewriter();
  }

  startTypewriter() {
    // Guards a blur firing after teardown (e.g. a Shopify section re-render) from starting a
    // timer loop on a detached element.
    if (!this.isConnected) return;
    if (!this.twPhrases || this.twTimer) return;
    // Don't animate over a real query (placeholder is hidden then anyway).
    if (this.input.value) return;
    this.twIndex = 0;
    this.input.setAttribute('placeholder', this.basePlaceholder);
    this.twTimer = setTimeout(() => this.typewriterNext(), TW_HOLD);
  }

  stopTypewriter(resetPlaceholder) {
    if (this.twTimer) {
      clearTimeout(this.twTimer);
      this.twTimer = null;
    }
    if (resetPlaceholder && this.basePlaceholder != null) {
      this.input.setAttribute('placeholder', this.basePlaceholder);
    }
  }

  typewriterNext() {
    const current = this.twPhrases[this.twIndex];
    const nextIndex = (this.twIndex + 1) % this.twPhrases.length;
    const next = this.twPhrases[nextIndex];

    if (this.twReduceMotion) {
      this.twIndex = nextIndex;
      this.input.setAttribute('placeholder', next);
      this.twTimer = setTimeout(() => this.typewriterNext(), TW_HOLD);
      return;
    }

    // Delete the current phrase, then type the next one, then hold and repeat.
    let text = current;
    const deleteStep = () => {
      if (text.length > 0) {
        text = text.slice(0, -1);
        this.input.setAttribute('placeholder', text);
        this.twTimer = setTimeout(deleteStep, TW_DELETE);
      } else {
        typeStep();
      }
    };
    let typed = 0;
    const typeStep = () => {
      typed += 1;
      this.input.setAttribute('placeholder', next.slice(0, typed));
      if (typed < next.length) {
        this.twTimer = setTimeout(typeStep, TW_TYPE);
      } else {
        this.twIndex = nextIndex;
        this.twTimer = setTimeout(() => this.typewriterNext(), TW_HOLD);
      }
    };
    deleteStep();
  }
}

if (!customElements.get('mu-integrated-search')) {
  customElements.define('mu-integrated-search', MuIntegratedSearch);
}
